import 'regenerator-runtime/runtime'
import { ensureIntlLocalePolyfill } from './polyfills/intlLocale'
import { hydrateRoot, createRoot } from 'react-dom/client'
import { Provider as ReduxProvider } from 'react-redux'
import { StyleSheetManager } from 'styled-components'
import smoothscroll from 'smoothscroll-polyfill'
import { loadableReady } from '@loadable/component'
import Routes from 'components/App/Routes/Routes'
import config from 'constants/config'
import tokenLoader from 'api/preprocessors/tokenLoader'
import brandLoader from 'api/preprocessors/brandLoader'
import localeLoader from 'api/preprocessors/localeLoader'
import browserErrorLogger from 'api/interceptors/browserErrorLogger'
import { watchAnalytics } from './watcher/analytics'
import { watchAccount } from './watcher/account'
import { AppHistoryRouter as Router } from 'lib/routing/appRouting'
import Modal from 'react-modal'
import {
  registerRequestPreprocessor,
  registerRequestInterceptor,
} from 'api/requestUtils'
import initialiseUser from './initialiseUser'
import { GeoProvider } from 'contexts/geoContext'
import { HeaderStateContextProvider } from 'contexts/HeaderStateContext'
import { HelmetProvider } from 'react-helmet-async'
import type { DehydratedState } from '@tanstack/react-query'
import {
  Hydrate,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import { watchCheckout } from './watcher/checkout'
import { CurrencyProvider } from 'contexts/currencyContext'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { watchNavigation } from './watcher/navigation'
import { init as initialiseTrackers } from 'analytics/analytics'
import { canUsePurpose } from './consent/types'
import { clearUngrantedPurposeIdentifiers } from './consent/clearWithdrawnIdentifiers'
import { readConsentState } from './consent/deviceState'
import { synchroniseConsent } from './consent/synchroniseConsent'
import ConsentBanner from 'components/Consent/ConsentBanner'
import { isConsentRequiredOnClient } from './consent/clientScope'
import initialiseBroadcastListener from './initialiseBroadcastListener'
import initialiseStore from './initialiseStore'
import initialiseHistory from './initialiseHistory'
import { registerErrorReportStore } from 'services/errorReportingService'
import { registerCartTrackingStore } from 'analytics/snowplow/events'
import ModalProvider from 'contexts/ModalProvider'
import ScrollToTop from 'components/App/ScrollToTop'
import { ContactProvider } from 'contexts/contactContext'
import { MasterModalProvider } from 'contexts/MasterModalContext'
import { shouldForwardProp } from 'lib/styles/styledComponents'
import { AppLayoutContextProvider } from 'contexts/AppLayoutContext'
import initialiseSiteTakeover from './initialiseSiteTakeover'
import { MultiCartMachineProvider } from 'machines/cart/MultiCartMachineProvider'
import { CookiesContextProvider } from 'contexts/CookiesContext'
import DevPanelProvider from 'components/App/DevPanel/DevPanelProvider'
import { NuqsAdapter } from 'lib/utils/nuqsReactRouterAdapter'
import initialiseHistoricalActions from './initialiseHistoricalActions'
import { I18nProvider } from 'i18n/I18nContext'
import { AppMountedContextProvider } from 'contexts/AppMountedContext'
import { initUserMonitoring } from 'services/userMonitoring'
import { PrefersReducedMotionContextProvider } from 'contexts/PrefersReducedMotionContext'
import { getLocaleStaticJsHost } from '../lib/staticAssets/getLocaleStaticJsHost'
import initialiseLabRemoteConfig from './bootstrap/initialiseLabRemoteConfig'
import WeddingGiftRegistryProvider from 'weddings/contexts/WeddingGiftRegistryContext'
import { LabRemoteConfigKeys } from 'constants/lab'
import { getLanguageOptions } from 'i18n/util/locale'
import { isLEStaff } from 'selectors/accountSelectors'
import BrandedThemeProvider from 'lib/theme/BrandedThemeProvider'
import { applyExperimentPanelOverrides } from 'lib/experiments/experimentPanelPersistence'

ensureIntlLocalePolyfill()

window.__GHA_CLIENT_LOGGER__?.markClientBundleStarted()

smoothscroll.polyfill()
Modal.setAppElement('#app')

const initialState = window.__INITIAL_STATE__ as App.State
delete window.__INITIAL_STATE__

if (config.STATIC_JS_HOST) {
  __webpack_public_path__ = `${getLocaleStaticJsHost(
    config.STATIC_JS_HOST,
    process.env.BUILD_LOCALE,
  )}/`
}
// It's possible for our server router state to not match current history state
// e.g. a 3rd party script changes window location before the app finishes loading
// Remove the server generated router state to ensure our router state is up to date.
// @ts-ignore
delete initialState.router

const dehydratedState = window.__REACT_QUERY_STATE__ as DehydratedState
delete window.__REACT_QUERY_STATE__

const history = initialiseHistory(
  initialState.geo.currentRegionCode.toLowerCase(),
  initialState.geo.locale,
  getLanguageOptions(
    initialState.system.features.remoteConfigFlags?.[LabRemoteConfigKeys.languages],
    isLEStaff(initialState),
  ).languageOptions,
)
const store = initialiseStore(history, applyExperimentPanelOverrides(initialState));

// Add store to the window for debugging purposes.
// It's available through dev tools anyway, so no harm in unearthing it.
// This is in the rare instances we a non technical user to debug a redux state
// DO NOT ACCESS THIS IN CODE
(window as any).__DEBUG_REDUX_STATE__ = store.getState

initialiseBroadcastListener(store)
registerRequestInterceptor(browserErrorLogger())
registerRequestPreprocessor(tokenLoader(store))
registerRequestPreprocessor(brandLoader(config.BRAND))
registerRequestPreprocessor(localeLoader(store))

registerCartTrackingStore(store)
registerErrorReportStore(store)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 3 * 1000,
    },
  },
})

initialiseHistoricalActions(store)
initialiseLabRemoteConfig(store, config.LAB_REMOTE_CONFIG_KEYS)
initialiseSiteTakeover(store)
const isConsentRequired = isConsentRequiredOnClient()
const consentState = readConsentState()

if (isConsentRequired) {
  synchroniseConsent()
  clearUngrantedPurposeIdentifiers(consentState?.purposes ?? [])
}

if (!isConsentRequired || canUsePurpose(consentState, 'analytics')) {
  initialiseTrackers()
  initUserMonitoring()
}

window.dataLayer = window.dataLayer || []
if (!isConsentRequired || canUsePurpose(consentState, 'advertising')) {
  watchAnalytics(store, window.dataLayer)
}
initialiseUser(store)
watchCheckout(store)
watchNavigation(store)
watchAccount(store, queryClient)

function renderApp() {
  window.__GHA_CLIENT_LOGGER__?.markRenderStarted()
  const storeState = store.getState()

  const appTree = (
    <HelmetProvider>
      <ReduxProvider store={store} serverState={storeState}>
        <I18nProvider>
          <BrandedThemeProvider brand={config.BRAND}>
            <CookiesContextProvider>
              {isConsentRequired && <ConsentBanner />}
              <GeoProvider>
                <ContactProvider>
                  <CurrencyProvider>
                    <QueryClientProvider client={queryClient}>
                      {config.ENABLE_REACT_QUERY_DEV_TOOLS && (
                        <ReactQueryDevtools initialIsOpen={false} />
                      )}
                      <AppMountedContextProvider>
                        <DevPanelProvider>
                          <AppLayoutContextProvider>
                            <HeaderStateContextProvider>
                              <Hydrate state={dehydratedState}>
                                <StyleSheetManager
                                  shouldForwardProp={shouldForwardProp}
                                  enableVendorPrefixes
                                >
                                  <Router history={history}>
                                    <MultiCartMachineProvider>
                                      <ScrollToTop />
                                      <MasterModalProvider>
                                        {/*
                                          Nested ModalProviders were added in #16204 for one-layer-deep modals and
                                          restored in #35938. They do not enable stacking on their own:
                                          useContext(ModalContext) always resolves to the inner provider, so multiple
                                          showModal calls still share one queue unless callers explicitly use different
                                          layers.
                                        */}
                                        <ModalProvider>
                                          {/* Inner ModalProvider is intentional (#16204, restored #35938) — gives Routes its own modal stack so modals using useLocation render inside <Router> */}
                                          <ModalProvider>
                                            <NuqsAdapter>
                                              <WeddingGiftRegistryProvider>
                                                <PrefersReducedMotionContextProvider>
                                                  <Routes />
                                                </PrefersReducedMotionContextProvider>
                                              </WeddingGiftRegistryProvider>
                                            </NuqsAdapter>
                                          </ModalProvider>
                                        </ModalProvider>
                                      </MasterModalProvider>
                                    </MultiCartMachineProvider>
                                  </Router>
                                </StyleSheetManager>
                              </Hydrate>
                            </HeaderStateContextProvider>
                          </AppLayoutContextProvider>
                        </DevPanelProvider>
                      </AppMountedContextProvider>
                    </QueryClientProvider>
                  </CurrencyProvider>
                </ContactProvider>
              </GeoProvider>
            </CookiesContextProvider>
          </BrandedThemeProvider>
        </I18nProvider>
      </ReduxProvider>
    </HelmetProvider>
  )

  return loadableReady().then(() => {
    window.__GHA_CLIENT_LOGGER__?.markLoadableReady()
    const appElement = document.getElementById('app')
    window.__GHA_CLIENT_LOGGER__?.markHydrationStarted()
    if (config.SSR_DISABLED) {
      const root = createRoot(appElement!)
      root.render(appTree)
    } else {
      hydrateRoot(appElement!, appTree)
    }
  })
}

// Add the ability to force a re-render of the app
// This is to support times when store data is manually manipulated
// and we want it to update on the site
// DO NOT ACCESS THIS IN CODE
(window as any).__DEBUG_RERENDER__ = renderApp

renderApp()
